// import { ReactComponent as WarningIcon } from '../../assets/Icons/adornments/Warning.svg';
import { ReactComponent as CloseIcon } from '../../assets/Icons/adornments/Close.svg';
import { ReactComponent as DeleteIcon } from '../../assets/Icons/adornments/Trash.svg';
import { ReactComponent as WarningIcon } from '../../assets/Icons/adornments/Warning.svg';
import { MouseEvent, useEffect, useState } from 'react';
import { Btn } from '../Buttons/Buttons';
import PopupFooter from './PopupFooter';
import { customInstance } from '../../api/custom-instance';
import { CircularProgress, Skeleton } from '@mui/material';
import ModalBase from './ModalBase';

export interface DeleteItemType {
    type: 'data_file' | 'dataset' | 'model' | 'model-family' | 'resultset' | 'prediction' | 'readacross' | 'job',
    id: string | number | { dag_id: string, run_id: string },
    name: string,
}

const itemsWithoutDataTree = ['resultset', 'readacross', 'job'];

export default function DeleteModal({
    items,
    cancelFn,
    deleteFn,
    handleCloseMenu,
}: {
    items: DeleteItemType[],
    cancelFn?: () => void,
    deleteFn?: (ids: (number | string | { dag_id: string, run_id: string })[]) => Promise<void>,
    handleCloseMenu?: () => void,
}) {
    const [open, setOpen] = useState(false);

    const [ btns, setBtns ] = useState<Btn[]>([]);
    const [ associatedItems, setAssociatedItems ] = useState<{
        datafiles: string[],
        datasets: string[];
        modelFamilies: string[];
        results: string[];
    } | null>(null);

    const [ isDataTreeLoading, setIsDataTreeLoading ] = useState(false);
    const [ isDeleteLoading, setIsDeleteLoading ] = useState(false);

    const handleOpen = () => {
        setOpen(true);
        if (items.length > 0 && !itemsWithoutDataTree.includes(items[0].type)) { 
            getAssociatedItems();
        }
    }

    const handleClose = () => {
        setOpen(false);
        cancelFn && cancelFn();
        resetValues();

        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    };

    const resetValues = () => {
        // setLabel('');
        setAssociatedItems(null);
        setIsDataTreeLoading(false);
    }

    const deleteItems = async (e: MouseEvent<HTMLButtonElement>, ids: (string | number | { dag_id: string, run_id: string })[]) => {
        e.preventDefault();
        setIsDeleteLoading(true);

        try {
            deleteFn && await deleteFn(ids);
            handleClose();
        } catch (err:any) {
            console.log(err);
        }

        setIsDeleteLoading(false);
    }

    useEffect(() => {
        const deleteLabel = items[0].type.split('_').join(' ');

        const cancelBtn: Btn = {
            type: "tertiary",
            styling: "outline",
            id: 'cancel-delete',
            label: 'Cancel',
            action: (e: MouseEvent<HTMLButtonElement>) => handleClose(),
        };

        setBtns([cancelBtn].concat({
            type: isDeleteLoading ? 'disabled' : 'warning',
            styling: "solid",
            id: 'delete-btn',
            label: `Yes, delete ${items.length > 1 ? 'these ' + deleteLabel + 's' : 'this ' + deleteLabel}`,
            startingAdornment: <DeleteIcon/>,
            action: async (e: MouseEvent<HTMLButtonElement>) => {e.preventDefault(); deleteItems(e, items.map(item => item.id))},
            isLoading: isDeleteLoading,
        }))

        //eslint-disable-next-line
    }, [items, cancelFn, isDeleteLoading]);

    const getDataTree = async (deleteItems: DeleteItemType[]) => {
        const type = deleteItems[0].type;

        const ids = deleteItems.map(item => item.id);

        try {
            // Batch requests to avoid overwhelming the API with concurrent heavy queries
            const BATCH_SIZE = 3;
            const results: { id: string | number | { dag_id: string, run_id: string }, data: any }[] = [];

            for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                const batch = ids.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(
                    batch.map(id => (
                        customInstance<{ data: any }>({
                            url: `${type}/tree/${id}`,
                            method: 'GET',
                        })
                        .then(data => ({ id, data }))
                    ))
                );
                results.push(...batchResults);
            }

            return results as unknown as { id: string, data: any }[];
        } catch (err:any) {
            console.log(err);
        }
    }

    const getAssociatedItems = async () => {
        setIsDataTreeLoading(true);

        const responses = await getDataTree(items);
        if (responses && responses.length > 0) {

            let datafiles: string[] = [];
            let datasets: string[] = [];
            let modelFamilies: string[] = [];
            let results: string[] = [];

            responses.forEach(response => {
                const data = response.data;
                const edges = data.edges;
                const nodes = data.nodes;

                let edgeIds: string[] = [];
                const edgeKeys = Object.keys(edges);
                edgeKeys.forEach(key => {
                    const values = edges[key];
                    edgeIds.push(values);
                });

                const nodeKeys = Object.keys(nodes);
                edgeIds.flat().forEach(edgeId => {
                    const matchingKey = nodeKeys.find(nodeKey => nodeKey === edgeId);
                    if (matchingKey) {
                        const nodeObj = nodes[matchingKey];
                        const fileType = nodeObj.file_type;
                        const name = nodeObj.name;

                        if (fileType === 'datafile') {
                            datafiles.push(name);
                        } else if (fileType === 'dataset') {
                            datasets.push(name);
                        } else if (fileType === 'model') {
                            modelFamilies.push(name);
                        } else {
                            results.push(name);
                        }
                        
                    }
                });

            });

            if (!datafiles.length && !datasets.length && !modelFamilies.length && !results.length) {
                setAssociatedItems(null);
            } else {
                setAssociatedItems({
                    datafiles,
                    datasets,
                    modelFamilies: Array.from(new Set(modelFamilies)),
                    results,
                })
            }
        } 

        setIsDataTreeLoading(false);
    };


    // useEffect(() => {
    //     if (items.length > 0 && !itemsWithoutDataTree.includes(items[0].type)) { 
    //         getAssociatedItems();
    //     }

    //     //eslint-disable-next-line
    // }, [items]);
    

    return (
        <div className='w-full'> 
            <button onClick={handleOpen} className={`w-full px-5 py-3 text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                <div className='flex justify-start items-center gap-3'>
                    <p className='text-nowrap'>Delete</p>
                    {isDeleteLoading && (
                        <CircularProgress size='12px' color='inherit'/>
                    )}
                </div>
            </button>

            <ModalBase
                open={open}
                modalId="delete-modal"
            >
                <div className={`bg-white rounded-lg max-w-[400px] w-full popup-menu mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch'>

                        <div className={`w-full px-6 pt-6 flex justify-end items-center gap-3`}>
                            <button onClick={handleClose} className="text-disable hover:text-primary transition-all">
                                <div className="w-[18px] h-[18px]"><CloseIcon/></div>
                            </button>
                        </div>

                        <div className="w-full flex flex-col gap-8 px-6 pb-6 justify-start items-center">
                            <p className="font-semibold text-default text-[20px]">Delete {items.length > 1 ? `${items[0].type.split('_').join(' ')}s` : items[0].type.split('_').join(' ')}?</p>
                            {isDataTreeLoading ? (
                                <Skeleton variant='rounded' width='100%' height={80}/>
                            ) : (
                                <p className='text-default text-center text-wrap max-w-[400px]'>You are about to delete <span className='font-semibold break-all'>{items.length === 1 ? `"${items[0].name}"` : `${items.length} ${items[0].type.split('_').join(' ')}s`}</span>. <br/>This action cannot be undone.</p>
                            )}
                            {isDataTreeLoading ? (
                                <Skeleton variant='rounded' width='100%' height={200}/>
                            ) : (
                                <>
                                {associatedItems && (
                                    <div className='w-full rounded-xl bg-[#FFEBEB] max-h-[200px] overflow-y-auto'>
                                        <div className='w-full flex flex-col gap-8 p-6 justify-start items-start'>
                                            <div className='flex flex-col gap-4'>
                                                <div className='flex justify-start items-center gap-2'>
                                                    <div className='w-[18px] text-warning'><WarningIcon/></div>
                                                    <p className='font-semibold text-[#941E1E]'>Warning</p>
                                                </div>

                                                <p className='text-default font-medium'>The following items will also be deleted:</p>
                                            </div>

                                            {Object.keys(associatedItems).map(item => {
                                                const values = associatedItems[item as keyof object] as string[];
                                                if (values.length > 0) {
                                                    return (
                                                        <div className='flex flex-col gap-4 justify-start items-start text-default'>
                                                            <p className='font-semibold'>{item === 'datafiles' ? 'Data Files' : item === 'datasets' ? 'Datasets' : item === 'modelFamilies' ? 'Model Families' : 'Predictions'}</p>
                                                            <ul className='list-disc ml-4'>
                                                                {values.map(value => (
                                                                    <li>{value}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )
                                                } else {
                                                    return null;
                                                }
                                                
                                            })}
                                        </div>
                                    </div>
                                )}
                                </>
                            )}
                            
                        </div>

                        <PopupFooter 
                            hasOutline={false}
                            buttons={btns}
                        />

                    </div>
                </div>
            </ModalBase>
        </div>
    )
}